import React, {useMemo} from "react";
import {Paper} from "@mui/material";
import {formatMoney} from "../../../../../../../core/services/utils/utils";
import {AccountingTransactionTypes} from "../../../../../../../core/constants/enums";
import classnames from "classnames";
import SummaryActionCard from "../../../../../../components/app-specific/summary-cards/summary-action-card";
import AddressCard from "../../../../../../components/app-specific/address-card";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {Link} from "react-router-dom";
import Grid2 from "@mui/material/Unstable_Grid2";


const billInfoSummaryBodyIds = {
    subTotal: 'subTotal',
    discount: 'discount',
    tax: (i) => `tax-${i}`,
    total: 'total',
    transaction: (i) => `transaction-${i}`,
}

const billInfoSummaryActionIds = {
    openQuote: 'openQuote',
}

const UserShoppingCartViewSummarySection = ({
                                                cart,
                                                loading,
                                                openQuoteLink,
                                            }) => {
    const operatorAccessLevels = useAccessLevels();

    const billInfoSummaryBody = useMemo(() => {
            const res = [];
            const discountAmount = (cart?.totalItemsDiscountAmount ?? 0) + (cart?.discountAmount ?? 0);
            const transactions = cart?.appliedTransactions?.filter(e => e.transactionTypeName !== AccountingTransactionTypes.userOrder) ?? [];
            res.push({
                id: billInfoSummaryBodyIds.subTotal,
                title: 'Sub total',
                value: cart?.subtotalAmount,
                loadingValue: "loading...",
                format: formatMoney,
                action: {show: false},
            })
            if (discountAmount > 0) {
                res.push({
                    id: billInfoSummaryBodyIds.discount,
                    value: discountAmount,
                    title: 'Discount',
                    loadingValue: "loading...",
                    format: formatMoney,
                    action: {show: false},
                })
            }
            let taxI = 0;
            for (const tax of cart?.taxes ?? []) {
                res.push({
                    id: billInfoSummaryBodyIds.tax(taxI++),
                    title: <span className={classnames({'line-through': tax?.exemptNo?.length})}>
                    {tax.shortname ?? '--'} ({tax?.rate ?? '--'}%)
                </span>,
                    value: tax?.exemptNo?.length ? `exempt: ${tax?.exemptNo}` : formatMoney(tax.total),
                    loadingValue: "loading...",
                    action: {show: false},
                })
            }
            res.push({
                id: billInfoSummaryBodyIds.total,
                title: 'Total',
                value: cart?.newTotalAmount ?? cart?.totalAmount,
                loadingValue: "loading...",
                format: formatMoney,
                action: {show: false},
            });
            let transactionI = 0;
            for (const transaction of transactions) {
                res.push({
                    id: billInfoSummaryBodyIds.transaction(transaction?.id ?? --transactionI),
                    title: transaction?.transactionTypeDescription,
                    value: -(transaction?.amount ?? 0),
                    loadingValue: "loading...",
                    format: formatMoney,
                })
            }
            return res;
        },
        [
            cart?.appliedTransactions,
            cart?.discountAmount,
            cart?.newTotalAmount,
            cart?.subtotalAmount,
            cart?.taxes,
            cart?.totalAmount,
            cart?.totalItemsDiscountAmount,
        ])

    const billInfoSummaryActions = useMemo(() => [
            {
                id: billInfoSummaryActionIds.openQuote,
                value: 'Open Quote',
                show: !!cart && operatorAccessLevels.packages.sales.quoteInformation,
                disabled: !!loading,
                variant: 'contained',
                props: {
                    fullWidth: true,
                    size: 'medium',
                    sx: {
                        marginInline: 3,
                    },
                    component: Link,
                    to: openQuoteLink,
                    target: '_blank',
                },
            },
        ],
        [
            cart,
            loading,
            openQuoteLink,
            operatorAccessLevels.packages.sales.quoteInformation
        ])

    const billInfoSummary = useMemo(() => ({
        header: {
            title: 'Balance',
            value: cart?.balance,
            loadingValue: "Loading...",
            format: formatMoney,
        },
        body: billInfoSummaryBody,
        actions: billInfoSummaryActions,
    }), [billInfoSummaryBody, billInfoSummaryActions, cart?.balance])

    const shippingAddress = useMemo(() => !cart?.shippingAddress
            ? undefined
            : ({
                ...cart?.shippingAddress,
                streetAddress: cart?.shippingAddress?.fullAddress,
                title: "Shipping Address"
            })
        , [cart?.shippingAddress])

    const billingAddress = useMemo(() => !cart?.billingAddress
            ? undefined
            : ({
                ...cart?.billingAddress,
                streetAddress: cart?.billingAddress?.fullAddress,
                title: "Billing Address"
            })
        , [cart?.billingAddress])

    return (
        <>
            <SummaryActionCard
                className={'auto-height'}
                header={billInfoSummary.header}
                body={billInfoSummary.body}
                actions={billInfoSummary.actions}
                loadingCount={billInfoSummary.body.length}
                loading={loading}
            />
            <Paper className='paper w-100 p-4 mt-3'>
                <Grid2 container spacing={2} sx={{width: '100%'}}>
                    {
                        loading &&
                        Array(2).fill(null).map((_, e) => (
                            <Grid2 key={e}>
                                <div className={'loading extraLarge mb-4'} style={{width: 'min(90%, 380px)'}}>
                                    <div/>
                                </div>
                            </Grid2>
                        ))
                    }
                    {
                        !loading && shippingAddress &&
                        <Grid2 xs={12} sm={6} md={12}>
                            <AddressCard
                                readonly
                                address={shippingAddress}
                            />
                        </Grid2>
                    }
                    {
                        !loading && billingAddress &&
                        <Grid2 xs={12} sm={6} md={12}>
                            <AddressCard
                                readonly
                                address={billingAddress}
                            />
                        </Grid2>
                    }
                </Grid2>
            </Paper>
        </>
    );
}

export default UserShoppingCartViewSummarySection;
